import React, { useEffect, useMemo } from 'react';
// import Cleave from 'cleave.js/react';
import { Modal, ModalBody } from 'reactstrap';
import { FaFileExcel } from 'react-icons/fa';
import { Row, Col } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import { RootState } from 'slices';
import { ExportRange } from 'types/common/Range';
import { formatNumberWithCommas } from 'utils/maths/Str2Num';
import { setCustomersExportFilters } from 'slices/exportData/reducer';
import { useDispatch } from 'react-redux';
import { EXPORT_LIMIT } from './constants';
import Select from 'react-select';
import { CustomReactSelectStyles } from 'styles/common/singleSelectStyle';

interface CustomerExportSelectModalProps {
  show?: boolean;
  onConfirmClick: (values: ExportRange) => void;
  onCloseClick?: () => void;
  title?: string;
  message?: string;
  cancelBtnMsg?: string;
  confirmBtnMsg?: string;
  totalData: number;
  type?: number;
}

const CustomerExportSelectModal: React.FC<CustomerExportSelectModalProps> = ({
  show = false,
  onConfirmClick,
  onCloseClick,
  title = 'Are you sure?',
  message = 'Do you want to proceed with this action?',
  cancelBtnMsg = 'Cancel',
  confirmBtnMsg = 'Confirm',
  totalData,
  type = 0,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const exportDataState = useSelector((state: RootState) => state.ExportData);
  const { CustomersExportFilters } = exportDataState;

  const customerState = useSelector((state: RootState) => state.Customer);
  const { PageFilters } = customerState;

  const isDarkMode =
    useSelector((state: RootState) => state.Layout.layoutModeType) === 'dark';

  // const maxLimit = useMemo(
  //   () => (type === 1 ? totalData * PageFilters.branch.length : totalData),
  //   [totalData, PageFilters.branch.length, type]
  // );

  const maxLimit = useMemo(() => totalData, [totalData]);

  useEffect(() => {
    dispatch(
      setCustomersExportFilters((prev: ExportRange) => ({
        ...prev,
        end: maxLimit,
      }))
    );
  }, [maxLimit, dispatch]);

  const validationSchema = Yup.object().shape({
    start: Yup.number()
      .required(t('StartRequired'))
      .min(1, t('StartGreaterThanZero'))
      .max(Yup.ref('end'), t('StartLessThanOrEqualToEnd')),
    end: Yup.number()
      .required(t('EndRequired'))
      .min(Yup.ref('start'), t('EndGreaterThanOrEqualToStart'))
      .max(
        maxLimit,
        t('EndLessThanOrEqualToMax', {
          max: formatNumberWithCommas(maxLimit, 0),
        })
      ),
  });

  const formik = useFormik({
    initialValues: CustomersExportFilters,
    validationSchema,
    onSubmit: async (values) => {
      onConfirmClick(values);
    },
  });

  const { touched, validateForm } = formik;
  useEffect(() => {
    if (i18n.language && (touched.start || touched.end)) {
      validateForm();
    }
  }, [i18n.language, touched.start, touched.end, validateForm]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    formik.handleSubmit();
  };
  // const handleChangeNumberInput = (
  //   e: React.ChangeEvent<HTMLInputElement>,
  //   formik: FormikProps<ExportRange>
  // ) => {
  //   const { name, value } = e.target;

  //   const rawValue = cleanCommas(value);

  //   formik.setFieldValue(name, rawValue).then(async () => {
  //     formik.setFieldTouched(name, true);
  //   });
  // };

  //? ==== New ==== //
  const rangeOptions = useMemo(() => {
    const opts: { value: string; label: string }[] = [];
    if (!maxLimit || maxLimit < 1) return opts;

    let start = 1;
    while (start <= maxLimit) {
      const end = Math.min(start + EXPORT_LIMIT - 1, maxLimit);
      const label = `${formatNumberWithCommas(
        start,
        0
      )} - ${formatNumberWithCommas(end, 0)}`;
      // const isMax = end === maxLimit;
      // const label =
      //   `${formatNumberWithCommas(start, 0)} - ${formatNumberWithCommas(
      //     end,
      //     0
      //   )}` +
      //   (isMax && maxLimit % EXPORT_LIMIT !== 0
      //     ? ` (${t('Max') || 'Max'})`
      //     : '');
      opts.push({ value: `${start}:${end}`, label });
      start = end + 1;
    }
    return opts;
  }, [maxLimit]);

  // derive currently selected option
  const selectedRangeOption = useMemo(() => {
    if (formik.values.start && formik.values.end) {
      const key = `${formik.values.start}:${formik.values.end}`;
      return rangeOptions.find((opt) => opt.value === key) || null;
    }
    return null;
  }, [formik.values.start, formik.values.end, rangeOptions]);

  // handle change
  const handleRangeChange = (
    option: { value: string; label: string } | null
  ) => {
    if (option) {
      const [s, e] = option.value.split(':').map(Number);
      formik.setFieldValue('start', s, false);
      formik.setFieldValue('end', e, false);
      formik.setFieldTouched('start', true, false);
      formik.setFieldTouched('end', true, false);
      formik.validateForm();
    }
  };

  return (
    <Modal fade isOpen={show} toggle={onCloseClick} centered>
      <ModalBody className="py-3 px-5 text-center">
        <div className="mt-2">
          <FaFileExcel size={30} className="mx-1 text-info" />
          <div className="mt-4 pt-2 fs-15">
            <h4>{title}</h4>
            <p className="text-muted mt-3 mb-0">{message}</p>

            <p className="text-bolder mt-2 mb-3">
              {t('EstimatedRowCount', {
                total:
                  type === 1
                    ? formatNumberWithCommas(
                        totalData * PageFilters.branch.length,
                        0
                      )
                    : formatNumberWithCommas(totalData, 0),
              })}
            </p>
          </div>
        </div>
        <form onSubmit={handleSubmit} noValidate>
          <Row className="align-items-start">
            {/* <Col lg={6} className="mb-2">
              <Label for="start">{t('Start')}</Label>

              <Cleave
                className={`form-control`}
                name={`start`}
                placeholder={t('ExportStartRange')}
                value={formik.values.start || ''}
                options={{
                  numeral: true,
                  numeralDecimalScale: 0,
                  numeralThousandsGroupStyle: 'thousand',
                }}
                // onChange={formik.handleChange}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleChangeNumberInput(e, formik)
                }
                onBlur={formik.handleBlur}
              />

              {formik.touched.start && formik.errors.start && (
                <div className="text-danger text-start">
                  {formik.errors.start}
                </div>
              )}
            </Col> */}
            {/* 
            <Col lg={6} className="mb-2">
              <Label for="end">{t('End')}</Label>
              <Cleave
                className={`form-control`}
                name={`end`}
                placeholder={t('ExportEndRange')}
                value={formik.values.end || ''}
                options={{
                  numeral: true,
                  numeralDecimalScale: 0,
                  numeralThousandsGroupStyle: 'thousand',
                }}
                // onChange={formik.handleChange}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleChangeNumberInput(e, formik)
                }
                onBlur={formik.handleBlur}
              />
              {formik.touched.end && formik.errors.end && (
                <div className="text-danger text-start">
                  {formik.errors.end}
                </div>
              )}
            </Col> */}
            <Col lg={2}></Col>
            <Col lg={8} className="mb-2 text-start">
              <Select
                name="range"
                styles={CustomReactSelectStyles(isDarkMode)}
                classNamePrefix="react-select"
                options={rangeOptions}
                value={selectedRangeOption}
                onChange={handleRangeChange}
                placeholder={t('Select Range')}
              />
              {formik.touched.start && formik.errors.start && (
                <div className="text-danger mt-1">{formik.errors.start}</div>
              )}
              {formik.touched.end && formik.errors.end && (
                <div className="text-danger mt-1">{formik.errors.end}</div>
              )}
            </Col>
            <Col lg={2}></Col>

            {/* <Col md={2}>
          <Button type="submit" color="success" outline className="mt-4">
            {t('Export')}
          </Button>
        </Col> */}
            <div className="d-flex gap-2 justify-content-center mt-4 mb-2">
              <button
                type="button"
                className="btn w-sm btn-light material-shadow-none"
                onClick={onCloseClick}
                data-testid="cancel-button"
              >
                {cancelBtnMsg}
              </button>
              <button
                type="submit"
                className="btn w-sm btn-primary material-shadow-none"
                // onClick={onConfirmClick}
                data-testid="confirm-button"
              >
                {confirmBtnMsg}
              </button>
            </div>
          </Row>
        </form>
      </ModalBody>
    </Modal>
  );
};

export default CustomerExportSelectModal;
